/**
 * React hook for using the API client in embedded routes
 *
 * Similar to useApiClient but uses cookie-based auth (no Clerk tokens).
 * Embedded routes authenticate via session cookies, not JWT tokens.
 */

import { useMemo } from 'react';
import { useEmbeddedAuthFetch } from './useEmbeddedAuthFetch';
import { ApiClient, API_ENDPOINTS } from '@/lib/api';

/**
 * Hook for accessing the centralized API client in embedded routes
 *
 * Returns:
 * - apiClient: Type-safe API client with methods for all endpoints
 * - endpoints: API endpoint constants (for backward compatibility)
 *
 * Audio blob downloads work via `fetchBlob` (cookie/session auth + /embedded/*
 * rewrite), passed as the ApiClient's blobFetch. getAuthData stays undefined
 * because embedded routes use cookie auth, not Bearer tokens.
 */
export function useEmbeddedApiClient() {
  const { authenticatedFetch, fetchBlob } = useEmbeddedAuthFetch();

  // Create ApiClient instance (memoized to prevent recreation on every render).
  // getAuthData = undefined (cookie auth, no Bearer); blobFetch handles binary
  // downloads (record audio) through the embedded session.
  const apiClient = useMemo(() => {
    return new ApiClient(authenticatedFetch, undefined, undefined, fetchBlob);
  }, [authenticatedFetch, fetchBlob]);

  return {
    apiClient,
    endpoints: API_ENDPOINTS,
  };
}
